import { useState, useEffect } from 'react'
import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material'
import { OrganizationService } from '@shared/services/OrganizationService'
import { Organization } from '@shared/types/entities'

interface OrganizationWithChildSites extends Organization {
  childSites?: Organization[]
}

interface OrganizationFilterProps {
  value: string
  onChange: (organizationId: string, childSiteId: string) => void
  label?: string
  size?: 'small' | 'medium'
}

export function OrganizationFilter({
  value,
  onChange,
  label = 'Filter by Organisation / Site',
  size = 'small'
}: OrganizationFilterProps) {
  const [organizations, setOrganizations] = useState<OrganizationWithChildSites[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      const data = await OrganizationService.getOrganizations()
      // Show all organizations and child sites (including inactive ones for filtering)
      setOrganizations(data || [])
    } catch (error) {
      console.error('Failed to load organisations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value

    if (selectedValue === 'all') {
      onChange('', '')
    } else if (selectedValue.startsWith('org-')) {
      // Organization selected
      const orgId = selectedValue.substring(4)
      onChange(orgId, '')
    } else if (selectedValue.startsWith('site-')) {
      // Child site selected
      const siteId = selectedValue.substring(5)
      // Find the organization that contains this child site
      const parentOrg = organizations.find(org =>
        org.childSites?.some(site => site.childSiteId === siteId)
      )
      onChange(parentOrg?.organizationId || '', siteId)
    }
  }

  // Build the current value for the select
  const selectValue = () => {
    if (!value) return 'all'

    // Check if it's a child site ID
    const isChildSite = organizations.some(org =>
      org.childSites?.some(site => site.childSiteId === value)
    )

    if (isChildSite) {
      return `site-${value}`
    }

    // It's an organization ID
    return `org-${value}`
  }

  return (
    <FormControl size={size} sx={{ minWidth: 250 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectValue()}
        label={label}
        onChange={handleChange}
        disabled={loading}
      >
        <MenuItem value="all">
          <em>All Organisations & Sites</em>
        </MenuItem>

        {organizations.map((org) => [
          <MenuItem key={`org-${org.organizationId}`} value={`org-${org.organizationId}`}>
            <strong>{org.name}</strong>
          </MenuItem>,
          ...(org.childSites || []).map((site) => (
            <MenuItem
              key={`site-${site.childSiteId}`}
              value={`site-${site.childSiteId}`}
              sx={{ pl: 4 }}
            >
              → {site.name}
            </MenuItem>
          ))
        ])}
      </Select>
    </FormControl>
  )
}
